import PageBackTitle from "../../Component/PageBackTitle";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import Select from "react-select";
import { HiOutlinePlus } from "react-icons/hi";
import { PiDotsThreeOutlineVerticalFill } from "react-icons/pi";
import { MdExpandLess, MdExpandMore, MdOutlineModeEditOutline } from "react-icons/md";
import { TbRefresh } from "react-icons/tb";
import { IoSearchOutline } from "react-icons/io5";
import useClickOutside from "../../useClickOutside";
import { hrRecordsApi } from "../../Services/hrRecordsApi";
import { buildSimpleOptions, formatDateOnlyIST } from "../hrRecordsHelpers";
import "../../tables/User.css";

async function fetchAllSessions() {
  const first = await hrRecordsApi.listTrainingSessions({ page: 1, per_page: 200 });
  const total = first.total_items || 0;
  const pages = Math.max(1, Math.ceil(total / 200));
  const pageRequests = [];

  for (let page = 2; page <= pages; page += 1) {
    pageRequests.push(hrRecordsApi.listTrainingSessions({ page, per_page: 200 }));
  }

  const results = await Promise.all(pageRequests);
  const allSessions = [
    ...(first.items || []),
    ...results.flatMap((result) => result.items || []),
  ];

  return allSessions;
}

function TrainingParticipantsListPage({ isSidebarExpanded }) {
  const [participants, setParticipants] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [isSortPopup, setIsSortPopup] = useState(false);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortOrder, setSortOrder] = useState({});
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [severity, setSeverity] = useState("success");
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const popupRef = useRef(null);

  useClickOutside([containerRef, buttonRef], () => {
    setIsSortPopup(false);
    setSelectedRowIndex(null);
  });
  useClickOutside([popupRef, buttonRef], () => {
    setIsSortPopup(false);
    setSelectedRowIndex(null);
  });

  const showSnackbar = (message, nextSeverity = "success") => {
    setSnackbarMessage(message);
    setSeverity(nextSeverity);
    setSnackbarOpen(true);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const allSessions = await fetchAllSessions();
      setSessions(buildSimpleOptions(allSessions, "id", "session_code"));

      const detailResponses = await Promise.all(
        allSessions.map((session) => hrRecordsApi.getTrainingSession(session.id)),
      );

      const flattened = detailResponses.flatMap((session) =>
        (session.participants || []).map((participant) => ({
          ...participant,
          session_id: session.id,
          session_code: session.session_code,
          training_name: session.training_name,
        })),
      );

      setParticipants(flattened);
    } catch (error) {
      console.error("Error fetching participants:", error);
      showSnackbar("Failed to load participants", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const visibleParticipants = participants.filter((participant) => {
    const matchesSession = selectedSession
      ? participant.session_id === selectedSession.value
      : true;

    const matchesSearch =
      !searchInput.trim() ||
      [
        participant.person_name,
        participant.ec_no,
        participant.training_name,
        participant.session_code,
        participant.certificate_number,
      ]
        .filter(Boolean)
        .some((value) =>
          String(value).toLowerCase().includes(searchInput.trim().toLowerCase()),
        );

    return matchesSession && matchesSearch;
  });

  const handleSort = (column) => {
    const order =
      sortColumn === column && sortOrder[column] === "asc" ? "desc" : "asc";

    setSortColumn(column);
    setSortOrder((prevOrder) => ({ ...prevOrder, [column]: order }));
    setIsSortPopup(false);

    const sorted = [...participants].sort((a, b) => {
      const aValue = a[column]?.toString().toLowerCase() || "";
      const bValue = b[column]?.toString().toLowerCase() || "";
      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });
    setParticipants(sorted);
  };

  const handleDelete = async (participant) => {
    if (!window.confirm("Delete this training participant?")) {
      return;
    }

    try {
      await hrRecordsApi.removeTrainingParticipant(participant.participant_id);
      showSnackbar("Participant removed successfully");
      fetchData();
    } catch (error) {
      console.error("Error removing participant:", error);
      showSnackbar(
        error?.response?.data?.message || "Failed to remove participant",
        "error",
      );
    }
  };

  return (
    <>
      <div
        className={`data-list-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-list-content" ref={containerRef}>
          <div className="data-list-header">
            <div className="data-list-header-content">
              <PageBackTitle>All Training Participants</PageBackTitle>
              <div className="data-list-heading-content-right-side">
                <div className="dropdown-container" style={{ minWidth: "220px" }}>
                  <Select
                    options={sessions}
                    value={selectedSession}
                    onChange={setSelectedSession}
                    placeholder="Filter by Session"
                    isSearchable
                    isClearable
                  />
                </div>

                <div className="search-bar-container">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    className="search-bar"
                  />
                  <IoSearchOutline className="search-bar-icon" />
                </div>

                <Link to="/add-hr-training-participant" className="data-list-new">
                  <Tooltip title="New Training Participant" arrow>
                    <button className="new-button">
                      <HiOutlinePlus className="new-plus-icon" /> New
                    </button>
                  </Tooltip>
                </Link>

                <Tooltip title="Refresh List" arrow>
                  <button className="refresh-button" onClick={fetchData}>
                    <TbRefresh className="refresh-icon" />
                  </button>
                </Tooltip>

                <button
                  className="sort-button"
                  onClick={() => setIsSortPopup(!isSortPopup)}
                  ref={buttonRef}
                >
                  <PiDotsThreeOutlineVerticalFill className="sort-icon" />
                </button>
                {isSortPopup && (
                  <div className="sort-popup-container" ref={containerRef}>
                    <div className="sort-popup-content">
                      <div className="sort-by">
                        SORT BY{" "}
                        {sortOrder[sortColumn] === "asc"
                          ? "(Ascending)"
                          : "(Descending)"}
                      </div>
                      <div className="sort-by-button">
                        <button onClick={() => handleSort("person_name")}>
                          Person Name{" "}
                          {sortOrder.person_name === "asc" ? (
                            <MdExpandMore />
                          ) : (
                            <MdExpandLess />
                          )}
                        </button>
                        <br />
                        <button onClick={() => handleSort("training_name")}>
                          Training Name{" "}
                          {sortOrder.training_name === "asc" ? (
                            <MdExpandMore />
                          ) : (
                            <MdExpandLess />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="data-list-body" onClick={() => setIsSortPopup(false)}>
            <div className="data-list-scroll-body">
              <div className="data-list-table">
                {loading ? (
                  <div className="loader-container">
                    <div className="loader"></div>
                    <div>Loading...</div>
                  </div>
                ) : visibleParticipants.length > 0 ? (
                  <table className="table multilevel-data">
                    <thead>
                      <tr>
                        <th className="center-alignment">SR. No.</th>
                        <th>PERSON NAME</th>
                        <th>EC NO.</th>
                        <th>TRAINING</th>
                        <th>SESSION</th>
                        <th>ISSUE DATE</th>
                        <th>NEXT DUE DATE</th>
                        <th>STATUS</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody className="data-list-scroll-bodys">
                      {visibleParticipants.map((participant, index) => (
                        <tr key={participant.participant_id} className="hoverable-row">
                          <td className="center-alignment">{index + 1}</td>
                          <td>{participant.person_name || "NA"}</td>
                          <td>{participant.ec_no || "NA"}</td>
                          <td>{participant.training_name || "NA"}</td>
                          <td>{participant.session_code || "NA"}</td>
                          <td>{formatDateOnlyIST(participant.issue_date)}</td>
                          <td>{formatDateOnlyIST(participant.next_due_date)}</td>
                          <td>{participant.status || "NA"}</td>
                          <td className="edit-pop">
                            <button
                              className="edit-data-button"
                              onClick={() =>
                                setSelectedRowIndex(
                                  selectedRowIndex === index ? null : index,
                                )
                              }
                            >
                              <div className="edit-button">
                                <MdExpandMore />
                              </div>
                            </button>
                          </td>
                          {selectedRowIndex === index && (
                            <div
                              className="edit-popup"
                              style={{
                                position: "absolute",
                                top: "69.5%",
                                right: "8%",
                              }}
                              ref={popupRef}
                            >
                              <div className="edit-popup-contant">
                                <div className="edit-popup-button">
                                  <Link
                                    to={`/edit-hr-training-participant/${participant.session_id}/${participant.participant_id}`}
                                    className="inquiry-new"
                                  >
                                    <button className="edit-button-inquiry">
                                      <MdOutlineModeEditOutline /> Edit
                                    </button>
                                  </Link>
                                  <button
                                    className="edit-button-inquiry"
                                    onClick={() => handleDelete(participant)}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-users-container">
                    <div className="no-users-message">
                      No Training Participants Found
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={() => setSnackbarOpen(false)} severity={severity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default TrainingParticipantsListPage;
